<template>
  <busy-button class="w-44"
               :variant="variant"
               :id="eventSaveButtonId"
               :is-loading="isBusy"
               :is-disabled="isEmptyStateTree || isDisabled"
               @click="saveEvent"
  >
    {{text}}
  </busy-button>
</template>

<script>
import eventService from '@/services/amio/event.service'
import { ROUTE_NAMES } from '@/router'
import BusyButton from '@/components/common/BusyButton'
import busyButtonsModule from '@/store/modules/busy-buttons.module'
import conversationModule from '@/store/modules/conversation.module'
import previewModule from '@/store/modules/preview.module'
import templateModule from '@/store/modules/template.module'
import errorUtils from '@/utils/error.utils'
import { BUSY_BUTTONS } from '@/utils/busy-buttons.constants'
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import clone from 'ramda/src/clone'
import { ITEM_IDS, SAVE_BUTTON_VARIANTS } from '@/utils/editor.constants'
import { SAVE_BUTTON_LABELS } from '@/utils/text.constants'
import variablesModule from '@/store/modules/variables.module'
import answerModule from '@/store/modules/answer.module'

export default {
  name: 'EventSaveButton',

  components: {
    BusyButton
  },

  props: {
    botId: {
      type: String,
      required: true
    },
    event: {
      type: Object,
      required: true
    },
    isDisabled: {
      type: Boolean,
      required: false
    }
  },

  watch: {
    'event.id'(newId, oldId) {
      window.clearTimeout(this.buttonStateTimeout)
      if (oldId !== ITEM_IDS.NEW) this.setButtonProps()
    }
  },

  data() {
    return {
      eventSaveButtonId: BUSY_BUTTONS.EVENT_SAVE,
      archivedTemplateRefs: {},
      archivedVariableRefs: {},
      archivedAnswerRefs: {},
      buttonStateTimeout: null,
      variant: SAVE_BUTTON_VARIANTS.DEFAULT,
      text: SAVE_BUTTON_LABELS.DEFAULT
    }
  },

  computed: {
    conversationJsons: conversationModule.getJsons,
    error: previewModule.error,
    stateTree: chatBubbleModule.stateTree,
    templateRefs: templateModule.templateRefs,
    variableRefs: variablesModule.variableRefs,
    answerRefs: answerModule.answerRefs,

    rootJson() {
      return conversationModule.getRootJson()
    },

    isBusy() {
      return busyButtonsModule.isButtonBusy(this.eventSaveButtonId)
    },

    isEmptyStateTree() {
      return previewModule.isEmptyStateTree(this.stateTree)
    }
  },

  methods: {
    async saveEvent() {
      this.saveTemplateRefs()
      this.saveVariableRefs()
      this.saveAnswerRefs()
      busyButtonsModule.setButtonBusy(this.eventSaveButtonId, true)
      this.error = null

      try {
        if (this.event.id !== ITEM_IDS.NEW) {
          await this.updateEvent()
        } else {
          await this.createEvent()
        }
      } catch (err) {
        // in case of failure, we should set template references to state before saving started
        templateModule.restoreTemplateRefs(this.archivedTemplateRefs)
        variablesModule.restoreVariableRefs(this.archivedVariableRefs)
        answerModule.restoreAnswerRefs(this.archivedAnswerRefs)
        errorUtils.handleConversationDesignerError(err, this.botId, this.stateTree, 'Event', this.$route.fullPath)
      } finally {
        this.startButtonStateTimeout()
        busyButtonsModule.setButtonBusy(this.eventSaveButtonId, false)
      }
    },

    async createEvent() {
      const newEvent = await eventService.create(
        this.botId,
        this.event.type,
        this.event.name,
        this.event.description,
        this.rootJson,
        this.archivedTemplateRefs,
        this.archivedVariableRefs,
        this.archivedAnswerRefs
      )
      conversationModule.setArchivedConversationJson(newEvent.conversationJson)
      await Promise.all([
        conversationModule.setId(newEvent.id),
        conversationModule.setEvents({ [newEvent.id]: newEvent.conversationJson }),
        conversationModule.setEventsHashes({ [newEvent.id]: newEvent.conversationJsonHash })
      ])
      this.$emit('process-new-event', newEvent)
      this.$router.replace({
        name: ROUTE_NAMES.events,
        params: {
          botId: this.botId,
          eventId: newEvent.id
        }
      })
    },

    async updateEvent() {
      const patchData = {
        conversation_json: this.rootJson,
        conversation_json_hash: conversationModule.getRootJsonHash(),
        template_refs: this.archivedTemplateRefs,
        variable_refs: this.archivedVariableRefs,
        answer_refs: this.archivedAnswerRefs
      }
      const updatedEvent = await eventService.update(this.botId, this.event.id, patchData)
      conversationModule.setArchivedConversationJson(updatedEvent.conversationJson)
      await conversationModule.setEventsHashes({ [updatedEvent.id]: updatedEvent.conversationJsonHash })
    },

    // snapshot of template refs when save button is hit, to prevent reset of template refs after saving
    saveTemplateRefs() {
      this.archivedTemplateRefs = clone(this.templateRefs)
      templateModule.resetTemplateRefs()
    },

    saveVariableRefs() {
      this.archivedVariableRefs = clone(this.variableRefs)
      variablesModule.resetVariableRefs()
    },

    saveAnswerRefs() {
      this.archivedAnswerRefs = clone(this.answerRefs)
      answerModule.resetAnswerRefs()
    },

    startButtonStateTimeout() {
      this.setButtonProps(this.error ? 'ERROR' : 'SUCCESS')
      this.buttonStateTimeout = setTimeout(() => {
        this.setButtonProps()
      }, 3000)
    },

    setButtonProps(buttonState = 'DEFAULT') {
      this.variant = SAVE_BUTTON_VARIANTS[buttonState]
      this.text = SAVE_BUTTON_LABELS[buttonState]
    }
  }
}
</script>
